// src/pages/Home.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useSearchParams, useParams } from 'react-router-dom';
import apiClient from '../utils/api'; 
import { injectMixedContent } from '../utils/adInjector';

import { syncQuotesFromFirestore } from '../utils/quotesData';
import QuotesCard from '../components/QuotesCard';
import AdRecommendedCard from '../components/AdRecommendedCard';

import NewsCard from '../components/newsCard';

import AdCard from '../components/AdCard';
import WishesCard from '../components/WishesCard'; 

import SearchBar from '../components/SearchBar';
import Chatbot from '../components/Chatbot';
import AppTour from '../components/AppTour';
import { useAuth } from '../contexts/AuthContext';
import { App as CapacitorApp } from '@capacitor/app';
import { Toast } from '@capacitor/toast'; 
import CompleteProfileModal from '../components/CompleteProfileModal';

import { getFirestore, collection, query, where, orderBy, limit, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { app } from '../firebase-config';

// --- IMPORT UNIFIED CACHE ---
import { feedCache } from '../utils/feedCache';

import './Home.css';

class CardErrorBoundary extends React.Component {
    constructor(props) { super(props); this.state = { hasError: false }; }
    static getDerivedStateFromError() { return { hasError: true }; }
    componentDidCatch(error, info) { console.error('Card render crashed:', error, info); }
    render() {
        if (this.state.hasError) return null;
        return this.props.children;
    }
}

const normalizePostType = (type) => {
    if (!type) return '';
    const t = String(type).trim().toLowerCase();
    if (t === 'ap' || t === 'andhra pradesh') return 'Andhra Pradesh';
    if (t === 'ts' || t === 'telangana') return 'Telangana';
    if (t === 'india') return 'India';
    if (t === 'world') return 'World';
    return type;
};

const Home = ({ setActiveCommentPostId }) => {
    const [searchParams] = useSearchParams(); 
    const { quoteId } = useParams();
    const { currentUser } = useAuth();
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    
    // Read directly from the unified cache preloaded on splash screen
    const [userNewsContent, setUserNewsContent] = useState(feedCache.userNewsContent || []);
    const [userNewsLoading, setUserNewsLoading] = useState(false);
    const [userNewsError, setUserNewsError] = useState('');
    const [hasMore, setHasMore] = useState(feedCache.hasMore);
    const isFetching = useRef(false); 
    const lastTimestampRef = useRef(feedCache.lastTimestamp);
    const [filters, setFilters] = useState(feedCache.filters);
    
    const location = useLocation();
    const [runTour, setRunTour] = useState(false);
    const [fullImageUrl, setFullImageUrl] = useState(null);
    const [activeIndex, setActiveIndex] = useState(feedCache.activeIndex);
    const activeIndexRef = useRef(feedCache.activeIndex);
    const animatingRef = useRef(false);
    const stageRef = useRef(null);
    const touchStartY = useRef(0);
    const touchLastY = useRef(0);
    const cardRefsMap = useRef({});
    const lastBackPressTime = useRef(0);

    const userNewsContentRef = useRef(userNewsContent);
    const fetchUserContentRef = useRef(null);
    const filtersRef = useRef(filters);
    const isInitializedRef = useRef(false);

    const [quotesPool, setQuotesPool] = useState(feedCache.quotesPool || []);

    // Sync state mutations back to our unified global cache
    useEffect(() => {
        feedCache.userNewsContent = userNewsContent;
        userNewsContentRef.current = userNewsContent;
    }, [userNewsContent]);

    useEffect(() => {
        feedCache.activeIndex = activeIndex;
        activeIndexRef.current = activeIndex;
    }, [activeIndex]);

    useEffect(() => {
        feedCache.hasMore = hasMore;
    }, [hasMore]);

    useEffect(() => {
        feedCache.filters = filters;
        filtersRef.current = filters;
    }, [filters]);

    useEffect(() => {
        feedCache.quotesPool = quotesPool;
    }, [quotesPool]);

    const fetchUserContent = useCallback(async (currentFilters, isLoadMore = false) => {
        if (isFetching.current || (isLoadMore && !hasMore)) return;

        isFetching.current = true;
        if (!isLoadMore) {
            setUserNewsLoading(true);
            lastTimestampRef.current = null;
            feedCache.lastTimestamp = null;
            activeIndexRef.current = 0;   
            setActiveIndex(0);  
        }
        setUserNewsError('');
        
        try {
            const params = { limit: 20 };
            const { postType, state, district, constituency, mandal } = currentFilters;
            
            if (isLoadMore && lastTimestampRef.current) {
                params.lastTimestamp = lastTimestampRef.current;
            }
            
            if (mandal) params.mandal = mandal;
            else if (constituency) params.constituency = constituency;
            else if (district) params.district = district;
            else if (state) params.state = state;

            if (postType && postType !== 'All' && postType !== 'Local') {
                params.postType = normalizePostType(postType);
            }

            const adParams = { ...params };
            delete adParams.lastTimestamp;

            const newsPromise = apiClient.get('/user-news', { params });
            const adsPromise = apiClient.get('/ads', { params }).catch(() => ({ data: [] }));

            const [newsResponse, adsResponse] = await Promise.all([newsPromise, adsPromise]);
            const rawNews = newsResponse.data;

            if (rawNews.length < 20) {
                setHasMore(false);
            } else {
                setHasMore(true);
            }

            if (rawNews.length > 0) {
                lastTimestampRef.current = rawNews[rawNews.length - 1].timestamp;
                feedCache.lastTimestamp = lastTimestampRef.current;
                const processedNewItems = rawNews.map(item => ({
                    ...item,
                    timestamp: item.timestamp?._seconds 
                        ? new Date(item.timestamp._seconds * 1000) 
                        : new Date(item.timestamp || Date.now())
                }));

                // --- REPLACE THIS BLOCK INSIDE fetchUserContent ---
setUserNewsContent(prev => {
    // 💡 FIX 1: Strip ALL injected cards (Ads, Quotes, AdRecommended) to keep only pure news items
    const existingNewsOnly = prev.filter(item => !item.isAd && !item.isCustomQuote && !item.isAdRecommended);
    
    // Create a Set of existing IDs to prevent duplicate cards during pagination
    const existingIds = new Set(existingNewsOnly.map(item => item.id));

    const cleanProcessedItems = processedNewItems.filter(item => {
        // 💡 FIX 2: Skip items that already exist in the feed
        if (isLoadMore && existingIds.has(item.id)) return false;

        const isAdField = String(item.isAd).toLowerCase();
        const isAd = isAdField === 'true' || isAdField === '1' || item.isAd === true;
        
        const postTypeAd = String(item.postType).toLowerCase() === 'ad' || String(item.postType).toLowerCase() === 'advertisement';
        const categoryAd = String(item.category).toLowerCase() === 'ad' || String(item.category).toLowerCase() === 'advertisement';
        
        const hasAdProps = !!item.adId || !!item.targetUrl || !!item.adType;

        return !isAd && !postTypeAd && !categoryAd && !hasAdProps;
    });

    const filteredAds = (adsResponse.data || []).filter(ad => {
        const selectedPostType = postType || '';
        const selectedState = state || '';
        const selectedDistrict = district || '';
        const selectedConstituency = constituency || '';
        const selectedMandal = mandal || '';

        const getTargetList = (singleField, arrayField) => {
            const list = [];
            if (singleField) list.push(singleField);
            if (Array.isArray(arrayField)) list.push(...arrayField);
            return list.map(s => String(s).trim());
        };

        const adPostTypes = getTargetList(ad.postType, ad.targetPostTypes || ad.targetPostType);
        const adStates = getTargetList(ad.state, ad.targetStates || ad.targetState);
        const adDistricts = getTargetList(ad.district, ad.targetDistricts || ad.targetDistrict);
        const adConstituencies = getTargetList(ad.constituency, ad.targetConstituencies || ad.targetConstituency);
        const adMandals = getTargetList(ad.mandal, ad.targetMandals || ad.targetMandal);

        if (adPostTypes.length > 0) {
            if (selectedPostType && selectedPostType !== 'All') {
                if (!adPostTypes.includes(selectedPostType)) return false;
            }
        }

        if (adStates.length > 0) {
            const activeState = selectedState || (selectedPostType === 'Andhra Pradesh' || selectedPostType === 'Telangana' ? selectedPostType : '');
            if (activeState && !adStates.includes(activeState)) return false;
        }

        if (adDistricts.length > 0) {
            if (!selectedDistrict || !adDistricts.includes(selectedDistrict)) return false;
        }

        if (adConstituencies.length > 0) {
            if (!selectedConstituency || !adConstituencies.includes(selectedConstituency)) return false;
        }

        if (adMandals.length > 0) {
            if (!selectedMandal || !adMandals.includes(selectedMandal)) return false;
        }

        return true;
    });

    const allNews = isLoadMore ? [...existingNewsOnly, ...cleanProcessedItems] : cleanProcessedItems;
    return injectMixedContent(allNews, filteredAds, quotesPool, 3);
});
            } else if (!isLoadMore) {
                setUserNewsContent([]);
                setHasMore(false);
            }
            
        } catch (err) {
            console.error("Error fetching user content:", err);
            setUserNewsError('వార్తలను లోడ్ చేయడంలో విఫలమైంది.');
        } finally {
            setUserNewsLoading(false);
            isFetching.current = false;
        }
    }, [hasMore, quotesPool]);

    // --- HIGHLY OPTIMIZED RANGE-BASED STYLE APPLICATION ---
    const applyStyles = useCallback((index, progress) => {
        const items = userNewsContentRef.current;  
        if (!items || items.length === 0) return;

        // Restrict computation to cards within index - 2 to index + 2
        const start = Math.max(0, index - 2);
        const end = Math.min(items.length - 1, index + 2);

        // Hide off-screen DOM nodes to free layout thread
        Object.keys(cardRefsMap.current).forEach(key => {
            const inRange = items.slice(start, end + 1).some(item => (item.adId || item.id) === key);
            if (!inRange) {
                const el = cardRefsMap.current[key];
                if (el && el.style.display !== 'none') {
                    el.style.display = 'none';
                }
            }
        });

        // Mutate styles only on the active viewport subset
        for (let i = start; i <= end; i++) {
            const item = items[i];
            if (!item) continue;
            const key = item.adId || item.id;
            const el = cardRefsMap.current[key];
            if (!el) continue;

            const off = i - index;
            if (el.style.display !== 'block') {
                el.style.display = 'block';
            }

            if (off === 0) {
                const deg = progress * -90;
                const tz = Math.abs(progress) * -60;
                el.style.transform = `perspective(900px) rotateX(${deg}deg) translateZ(${tz}px)`;
                el.style.opacity = String(1 - Math.abs(progress) * 0.5);
                el.style.zIndex = '10';
            } else {
                const deg = 90 + progress * -90;
                const tz = Math.abs(progress) * -60;
                el.style.transform = `perspective(900px) rotateX(${deg}deg) translateZ(${tz}px)`;
                el.style.opacity = String(Math.abs(progress) * 0.9);
                el.style.zIndex = '5';
            }
        }
    }, []); 

    useEffect(() => {
        if (feedCache.userNewsContent && feedCache.userNewsContent.length > 0) {
            const timer = setTimeout(() => {
                applyStyles(activeIndexRef.current, 0);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [applyStyles]);

    const handlePrependPost = useCallback((newPost) => {
        setUserNewsContent(prev => {
            if (prev.some(item => item.id === newPost.id)) return prev;
            
            const processedPost = {
                ...newPost,
                timestamp: newPost.timestamp?._seconds 
                    ? new Date(newPost.timestamp._seconds * 1000) 
                    : new Date(newPost.timestamp || Date.now())
            };
            
            const updated = [processedPost, ...prev];
            userNewsContentRef.current = updated;
            feedCache.userNewsContent = updated;
            
            activeIndexRef.current += 1;
            setActiveIndex(activeIndexRef.current);
            feedCache.activeIndex = activeIndexRef.current;
            
            setTimeout(() => {
                applyStyles(activeIndexRef.current, 0);
            }, 50);
            
            return updated;
        });
    }, [applyStyles]);

    const handlePrependPostRef = useRef(handlePrependPost);
    useEffect(() => {
        handlePrependPostRef.current = handlePrependPost;
    }, [handlePrependPost]);

    useEffect(() => {
        fetchUserContentRef.current = fetchUserContent;
    }, [fetchUserContent]);

    useEffect(() => {
        const db = getFirestore(app);
        
        const unsubscribeQuotes = onSnapshot(collection(db, "quotes"), (snapshot) => {
            const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setQuotesPool(list);
            syncQuotesFromFirestore(list); 
        }, (error) => {
            console.error("Quotes collection sync error:", error);
        });

        return () => {
            unsubscribeQuotes();
        };
    }, []);

    useEffect(() => {
        filtersRef.current = filters;
    }, [filters]);

    useEffect(() => {
        const db = getFirestore(app);
        let isInitialSnapshot = true;
        const initialDocIds = new Set();

        const approvedQuery = query(
            collection(db, "userNews"),
            where("status", "==", "approved"),
            orderBy("timestamp", "desc"),
            limit(10)
        );

        const unsubscribeApproved = onSnapshot(approvedQuery, (snapshot) => {
            if (isInitialSnapshot) {
                snapshot.docs.forEach(doc => initialDocIds.add(doc.id));
                isInitialSnapshot = false;
                return;
            }

            snapshot.docChanges().forEach((change) => {
                const postData = change.doc.data();
                const docId = change.doc.id;

                if (change.type === "modified") {
                    setUserNewsContent(prev => {
                        const exists = prev.some(item => item.id === docId);
                        if (exists) {
                            return prev.map(item => {
                                if (item.id === docId) {
                                    return {
                                        ...item,
                                        ...postData,
                                        timestamp: postData.timestamp?._seconds 
                                            ? new Date(postData.timestamp._seconds * 1000) 
                                            : new Date(postData.timestamp || Date.now())
                                    };
                                }
                                return item;
                            });
                        }
                        return prev;
                    });
                }

                if (change.type === "added" || change.type === "modified") {
                    if (!initialDocIds.has(docId) && postData?.status === "approved") {
                        const approvedPost = { id: docId, ...postData };
                        initialDocIds.add(docId);

                        if (handlePrependPostRef.current) {
                            handlePrependPostRef.current(approvedPost);
                        }
                    }
                }
            });
        }, (error) => {
            console.error("Approved news real-time sync error:", error);
        });

        return () => {
            unsubscribeApproved();
        };
    }, []);

    useEffect(() => {
        const db = getFirestore(app);

        let unsubscribeUserPending = () => {};
        if (currentUser?.uid) {
            const userPendingQuery = query(
                collection(db, "userNews"),
                where("userId", "==", currentUser.uid),
                where("status", "==", "pending")
            );

            unsubscribeUserPending = onSnapshot(userPendingQuery, (snapshot) => {
                snapshot.docChanges().forEach((change) => {
                    const postData = change.doc.data();
                    
                    if (change.type === "removed" || (change.type === "modified" && postData && postData.status !== "pending")) {
                        
                        if (postData && postData.status === "approved") {
                            const approvedPost = { id: change.doc.id, ...postData };
                            if (handlePrependPostRef.current) {
                                handlePrependPostRef.current(approvedPost);
                            }
                        } else {
                            if (fetchUserContentRef.current) {
                                fetchUserContentRef.current(filtersRef.current);
                            }
                        }
                    }
                });
            }, (error) => {
                console.error("User pending sync error:", error);
            });
        }

        return () => {
            unsubscribeUserPending();
        };
    }, [currentUser]);

    const goNext = useCallback(() => {
        if (animatingRef.current) return;
        if (activeIndexRef.current >= userNewsContentRef.current.length - 1) return;
        animatingRef.current = true;
        Object.values(cardRefsMap.current).forEach(el => {
            if (el) el.style.transition = 'transform 0.38s cubic-bezier(0.25,0.46,0.45,0.94), opacity 0.38s ease';
        });
        applyStyles(activeIndexRef.current, -1);
        setTimeout(() => {
            activeIndexRef.current += 1;
            setActiveIndex(activeIndexRef.current);
            Object.values(cardRefsMap.current).forEach(el => {
                if (el) el.style.transition = 'none';
            });
            applyStyles(activeIndexRef.current, 0);
            animatingRef.current = false;
            const threshold = userNewsContentRef.current.length - 6;
            if (activeIndexRef.current >= threshold && !isFetching.current) {
                fetchUserContentRef.current?.(filtersRef.current, true);  
            }
        }, 380);
    }, [applyStyles]);

    const goPrev = useCallback(() => {
        if (animatingRef.current) return;
        if (activeIndexRef.current <= 0) return;
        animatingRef.current = true;
        Object.values(cardRefsMap.current).forEach(el => {
            if (el) el.style.transition = 'transform 0.38s cubic-bezier(0.25,0.46,0.45,0.94), opacity 0.38s ease';
        });
        activeIndexRef.current -= 1;
        setActiveIndex(activeIndexRef.current);
        applyStyles(activeIndexRef.current, 0);
        setTimeout(() => { animatingRef.current = false; }, 380);
    }, [applyStyles]); 

    useEffect(() => {
        userNewsContentRef.current = userNewsContent;
        applyStyles(activeIndexRef.current, 0);
    }, [userNewsContent, applyStyles]);

    useEffect(() => {
        const stage = stageRef.current;
        if (!stage) return;
        let startY = 0;
        let startX = 0;
        let lastDelta = 0;
        let isMoving = false;
        let isHorizontalSwipe = false;

        const onTouchStart = (e) => {
            startY = e.touches[0].clientY;
            startX = e.touches[0].clientX;
            lastDelta = 0;
            isMoving = false;
            isHorizontalSwipe = false;
        };

        const onTouchMove = (e) => {
            const dy = e.touches[0].clientY - startY;
            const dx = e.touches[0].clientX - startX;
            lastDelta = dy;

            if (!isMoving) {
                if (Math.abs(dy) > 10 || Math.abs(dx) > 10) {
                    isMoving = true;
                    if (Math.abs(dx) > Math.abs(dy) && e.target.closest('.quotes-card-container')) {
                        isHorizontalSwipe = true; 
                    } else {
                        Object.values(cardRefsMap.current).forEach(el => {
                            if (el) el.style.transition = 'none';
                        });
                    }
                }
            }

            if (isHorizontalSwipe) {
                return; 
            }

            if (!isMoving) return;

            const progress = Math.max(-1, Math.min(1, dy / stage.clientHeight));
            applyStyles(activeIndexRef.current, -progress);
        };

        const onTouchEnd = () => {
            if (isHorizontalSwipe) return;
            if (!isMoving) return;

            if (lastDelta < -60) goNext();
            else if (lastDelta > 60) goPrev();
            else {
                Object.values(cardRefsMap.current).forEach(el => {
                    if (el) el.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
                });
                applyStyles(activeIndexRef.current, 0);
            }
        };

        const onWheel = (e) => {
            e.preventDefault();
            if (e.deltaY > 25) goNext();
            else if (e.deltaY < -25) goPrev();
        };

        stage.addEventListener('touchstart', onTouchStart, { passive: true });
        stage.addEventListener('touchmove', onTouchMove, { passive: true });
        stage.addEventListener('touchend', onTouchEnd, { passive: true });
        stage.addEventListener('wheel', onWheel, { passive: false });
        return () => {
            stage.removeEventListener('touchstart', onTouchStart);
            stage.removeEventListener('touchmove', onTouchMove);
            stage.removeEventListener('touchend', onTouchEnd);
            stage.removeEventListener('wheel', onWheel);
        };
    }, [applyStyles, goNext, goPrev]);

    useEffect(() => {
        const routeQuoteId = quoteId; 
        const searchQuoteId = searchParams.get('openQuote'); 
        const quoteIdToOpen = routeQuoteId || searchQuoteId;

        if (!quoteIdToOpen) return;

        const handleQuoteDeepLink = () => {
            const foundIndex = userNewsContent.findIndex(item => 
                item.isCustomQuote && (item.id === `quote_${quoteIdToOpen}` || item.id === quoteIdToOpen)
            );

            if (foundIndex !== -1) {
                activeIndexRef.current = foundIndex;
                setActiveIndex(foundIndex);
                setTimeout(() => {
                    applyStyles(foundIndex, 0);
                }, 150);
            }
            window.history.replaceState({}, '', '/');
        };

        if (userNewsContent.length > 0) {
            handleQuoteDeepLink();
        }
    }, [userNewsContent, searchParams, quoteId, applyStyles]);

    useEffect(() => {
        const postIdToOpen = searchParams.get('openComments');
        if (!postIdToOpen) return;

        const handleDeepLinkSync = async () => {
            if (typeof setActiveCommentPostId === 'function') {
                setActiveCommentPostId(postIdToOpen);
            }

            const foundIndex = userNewsContent.findIndex(item => item.id === postIdToOpen);
            
            if (foundIndex !== -1) {
                activeIndexRef.current = foundIndex;
                setActiveIndex(foundIndex);
                setTimeout(() => {
                    applyStyles(foundIndex, 0);
                }, 150);
            } else {
                try {
                    const db = getFirestore(app);
                    const docRef = doc(db, "userNews", postIdToOpen);
                    const docSnap = await getDoc(docRef);

                    if (docSnap.exists()) {
                        const newPost = {
                            id: docSnap.id,
                            ...docSnap.data(),
                            timestamp: docSnap.data().timestamp?._seconds 
                                ? new Date(docSnap.data().timestamp._seconds * 1000) 
                                : new Date(docSnap.data().timestamp || Date.now())
                        };

                        setUserNewsContent(prev => {
                            const filtered = prev.filter(item => item.id !== postIdToOpen);
                            return [newPost, ...filtered];
                        });

                        activeIndexRef.current = 0;
                        setActiveIndex(0);
                        setTimeout(() => {
                            applyStyles(0, 0);
                        }, 150);
                    }
                } catch (error) {
                    console.error("Error fetching deep-linked post:", error);
                }
            }

            window.history.replaceState({}, '', '/');
        };

        if (userNewsContent.length > 0) {
            handleDeepLinkSync();
        }
    }, [userNewsContent, searchParams, setActiveCommentPostId, applyStyles]);

    useEffect(() => {
        if (!window.Capacitor) return;

        const backButtonListener = CapacitorApp.addListener('backButton', ({ canGoBack }) => {
            if (location.pathname === '/') {
                const currentTime = Date.now();
                if (currentTime - lastBackPressTime.current < 2000) {
                    CapacitorApp.exitApp();
                } else {
                    lastBackPressTime.current = currentTime;
                    Toast.show({
                        text: 'వార్తల నుండి నిష్క్రమించడానికి మళ్ళీ నొక్కండి (Click again to exit)',
                        duration: 'short',
                        position: 'bottom'
                    });
                }
            } else {
                window.history.back();
            }
        });

        return () => {
            backButtonListener.then(handle => handle.remove());
        };
    }, [location.pathname]);

    useEffect(() => {
        const hasSeenTour = localStorage.getItem('hasSeenAppTour_v1');
        if (!hasSeenTour) {
            setRunTour(true);
        }
    }, []);

    // 💡 FIX 3: Add prevFiltersRef above the useEffect
    const prevFiltersRef = useRef(filters);

    useEffect(() => {
        // Check if search filters actually changed
        const filtersChanged = JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters);
        prevFiltersRef.current = filters;

        // On initial component mount:
        if (!isInitializedRef.current) {
            isInitializedRef.current = true;
            // If cached feed exists, preserve card position and skip refetching
            if (feedCache.userNewsContent && feedCache.userNewsContent.length > 0) {
                return; 
            }
            fetchUserContent(filters);
            return;
        }

        // Only trigger a fresh fetch if the user manually changed search filters
        if (filtersChanged) {
            fetchUserContent(filters);
        }
    }, [filters, fetchUserContent]);
    
    const tourSteps = [
        {
            targetId: currentUser ? 'tour-post-news' : 'tour-login',
            content: currentUser 
                ? 'వార్తలను మరియు ప్రకటనలను పోస్ట్ చేయడానికి ఇక్కడ నొక్కండి.' 
                : 'వార్తలను పోస్ట్ చేయడానికి ముందుగా లాగిన్ అవ్వండి.'
        },
        {
            targetId: 'tour-chatbot',
            content: 'వార్తలు రాయడంలో సహాయం కావాలంటే ఈ రోబోని అడగండి.'
        },
        {
            targetId: 'tour-news-card',
            content: 'ముఖ్యాంశాల కోసం ఇక్కడ చూడండి. వార్తలను మార్చడానికి పైకి లేదా క్రిందికి స్వైప్ (Swipe) చేయండి.'
        }
    ];

    useEffect(() => {
        const justFinished = localStorage.getItem('profile_completed_session');
        if (currentUser?.profile && justFinished !== 'true') {
            const p = currentUser.profile;
            const isMissingData = 
                !p.gender || 
                !p.age || 
                !p.dob || 
                !p.location?.state || 
                !p.location?.district || 
                !p.location?.constituency || 
                !p.location?.mandal ||
                !p.phoneNumber;
            
            if (isMissingData) {
                setShowCompletionModal(true);
            }
        }
    }, [currentUser]);

    return (
        <div className="home-page-focused">
            {runTour && (
                <AppTour 
                    steps={tourSteps} 
                    onComplete={() => {
                        localStorage.setItem('hasSeenAppTour_v1', 'true');
                        setRunTour(false);
                    }} 
                />
            )}

            {showCompletionModal && (
                <CompleteProfileModal 
                    user={currentUser.profile} 
                    uid={currentUser.uid}
                    onComplete={() => {
                        setShowCompletionModal(false);
                    }} 
                />
            )}

            <div className="home-header-fixed">
                <SearchBar 
                    filters={filters} 
                    setFilters={setFilters} 
                    onSearch={fetchUserContent} 
                />
            </div>

            <div className="home-news-viewport" id="tour-news-card" ref={stageRef}>
                {fullImageUrl && ReactDOM.createPortal(
                    <div
                        onClick={() => setFullImageUrl(null)}
                        style={{
                            position: 'fixed', inset: 0,
                            background: 'rgba(0,0,0,0.96)',
                            zIndex: 999999,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                    >
                        <img
                            src={fullImageUrl}
                            alt="Full view"
                            style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain' }}
                            onClick={e => e.stopPropagation()}
                        />
                        <span
                            onClick={() => setFullImageUrl(null)}
                            style={{
                                position: 'absolute', top: 40, right: 20,
                                color: '#fff', fontSize: 32,
                                cursor: 'pointer', padding: '8px 12px',
                            }}
                        >✕</span>
                    </div>,
                    document.body
                )}
                {!userNewsLoading && userNewsContent.length === 0 ? (
    <div className="no-data-found-fullscreen" style={{ padding: '40px 20px', textAlign: 'center' }}>
        {filters.postType === 'Following' ? (
            /* Custom Follower Guide in Telugu */
            <div className="follow-instruction-card" style={{ background: '#f0f7ff', border: '1px solid #d1e3ff', borderRadius: '12px', padding: '24px', maxWidth: '400px', margin: '0 auto' }}>
                <span style={{ fontSize: '32px' }}>📣</span>
                <h3 style={{ color: '#0056b3', marginTop: '10px', fontSize: '18px', fontWeight: 'bold' }}>మీకు నచ్చిన వారిని ఫాలో అవ్వండి!</h3>
                <p style={{ color: '#495057', fontSize: '14px', lineHeight: '1.6', margin: '12px 0' }}>
                    మీ ప్రాంతానికి చెందిన రిపోర్టర్స్ లేదా మీకు నచ్చిన స్నేహితులను ఫాలో అవ్వడం ద్వారా వారు పోస్ట్ చేసే తాజా వార్తలను మీరు ఇక్కడ నేరుగా పొందవచ్చు.
                </p>
                <div style={{ background: '#ffffff', padding: '12px', borderRadius: '8px', border: '1px dashed #007bff', fontSize: '13px', color: '#333' }}>
                    <strong>ఎలా వెతకాలి?</strong><br />
                    ప్రక్కన ఉన్న మెనూ బార్ క్లిక్ చేసి <strong>🔍 Search Users</strong> ఆప్షన్ ద్వారా ప్రొఫైల్స్ ని శోధించి ఫాలో అవ్వవచ్చు.
                </div>
            </div>
        ) : (
            /* Standard fallback */
            <>
                <h3>వార్తలు ఏవీ లేవు</h3>
                <p>ఈ విభాగంలో ప్రస్తుతం వార్తలు అందుబాటు లేదు.</p>
            </>
        )}
    </div>
) : (
                    <div className="cube-stage">
                        {userNewsContent.map((item, index) => {
                            const key = item.adId || item.id || `slide-${index}`;
                            const isActive = index === activeIndex;
                            const isWishesCard = (() => {
                                const checkMatch = (val) => {
                                    if (!val) return false;
                                    const checkStr = (str) => {
                                        const s = String(str).toLowerCase();
                                        return s.includes('wish') || s.includes('special') || s.includes('శుభాకాంక్షలు') || s.includes('పుట్టినరోజు');
                                    };
                                    if (Array.isArray(val)) {
                                        return val.some(item => checkStr(item));
                                    }
                                    return checkStr(val);
                                };

                                const hasExactMatch = (val) => {
                                    if (!val) return false;
                                    const targetTypes = ['special day wishes', 'specialdaywishes', 'wishes', 'ప్రత్యేక శుభాకాంక్షలు'];
                                    if (Array.isArray(val)) {
                                        return val.some(item => targetTypes.includes(String(item).toLowerCase()));
                                    }
                                    return targetTypes.includes(String(val).toLowerCase());
                                };

                                return (
                                    hasExactMatch(item.postType) ||
                                    checkMatch(item.postType) ||
                                    checkMatch(item.category) ||
                                    checkMatch(item.title)
                                );
                            })();
                            return (
                                <div
                                    key={key}
                                    className="cube-slide"
                                    ref={el => {
                                        if (el) cardRefsMap.current[key] = el;
                                        else delete cardRefsMap.current[key];
                                    }}
                                    style={{ display: 'none' }}
                                >
                                    <CardErrorBoundary>
                                        {item.isCustomQuote ? (
                                            <QuotesCard quotes={item.quotesData} />
                                        ) : 

                                        item.isAdRecommended ? (
                                            <AdRecommendedCard 
                                                adData={item.adData}
                                                recommendedNews={item.recommendedNews}
                                                isActive={isActive}
                                            />
                                        ) :
                                        
                                        item.isAd ? (
                                            <AdCard 
                                                adData={item} 
                                                isActive={isActive} 
                                            />
                                        ) : 

                                        isWishesCard ? (
                                            <WishesCard
                                                newsItem={{ ...item, description: item.content || item.description }}
                                                isActive={isActive}
                                                setActiveCommentPostId={setActiveCommentPostId}
                                            />
                                        ) : (
                                            <NewsCard
                                                newsItem={item}
                                                isFeedView={true}
                                                pageFilters={filters}
                                                setPageFilters={setFilters}
                                                setActiveCommentPostId={setActiveCommentPostId}
                                                isActive={isActive}
                                                onImageOpen={(url) => setFullImageUrl(url)}
                                            />
                                        )}
                                    </CardErrorBoundary>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Home;